import React, { useState, useEffect } from 'react';
import { supportService, CustomerOrder } from '../../../services/supportService';
import { Button, fieldClassName } from '../../../design-system';

interface OrderSelectorProps {
  onSelect: (orderId: number, itemIds: number[]) => void;
  selectedOrderId?: number | null;
  selectedItemIds?: number[];
}

export const OrderSelector: React.FC<OrderSelectorProps> = ({
  onSelect,
  selectedOrderId,
  selectedItemIds = [],
}) => {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async (cursor?: string) => {
    try {
      setLoading(true);
      const response = await supportService.getCustomerOrders(10, cursor);
      if (cursor) {
        setOrders(prev => [...prev, ...response.data]);
      } else {
        setOrders(response.data);
      }
      setNextCursor(response.next_cursor);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (orderId: number) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleItemToggle = (orderId: number, itemId: number) => {
    const currentItemIds = selectedOrderId === orderId ? selectedItemIds : [];
    const newItemIds = currentItemIds.includes(itemId)
      ? currentItemIds.filter(id => id !== itemId)
      : [...currentItemIds, itemId];
    
    onSelect(orderId, newItemIds);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const persianDate = new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    return persianDate;
  };

  const formatPrice = (price: number) => {
    return (price / 10).toLocaleString('fa-IR');
  };

  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.id.toString().includes(query) ||
      order.parcels.some(parcel =>
        parcel.items.some(item =>
          item.title.toLowerCase().includes(query) ||
          parcel.vendor.title.toLowerCase().includes(query)
        )
      )
    );
  });

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-xs text-ds-500">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3" dir="rtl">
      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="جست‌وجو"
          className={`${fieldClassName} pr-10`}
        />
        <svg
          className="absolute right-3 top-3 h-5 w-5 text-ds-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Orders List */}
      <div className="ds-scrollbar max-h-96 space-y-2 overflow-y-auto">
        {filteredOrders.map((order) => {
          const isExpanded = expandedOrders.has(order.id);
          const isSelected = selectedOrderId === order.id;
          return (
            <div
              key={order.id}
              className={`overflow-hidden rounded-ds-8 border ${
                isSelected
                  ? 'border-ds-primary bg-ds-primary-soft'
                  : 'border-ds-100 bg-ds-white'
              }`}
            >
              {/* Order Header */}
              <button
                type="button"
                className="w-full bg-ds-subtle px-3 py-2.5 text-right transition-colors hover:bg-ds-100 app-sm:px-4 app-sm:py-3"
                onClick={() => toggleOrder(order.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1 app-sm:flex-row app-sm:items-center app-sm:gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-ds-700">
                        شماره سفارش:
                      </span>
                      <span className="text-xs font-bold text-ds-ink">
                        {order.id.toLocaleString('fa-IR')}
                      </span>
                    </div>
                    <span className="text-[10px] text-ds-500">
                      تاریخ: {formatDate(order.created_at)}
                    </span>
                  </div>
                  <svg
                    className={`h-5 w-5 shrink-0 text-ds-400 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Order Content */}
              {isExpanded && (
                <div className="space-y-4 bg-ds-white p-3 app-sm:p-4">
                  {order.parcels.map((parcel) => (
                    <div key={parcel.id} className="space-y-3">
                      {/* Vendor Info */}
                      <div className="flex items-center gap-2 border-b border-ds-100 pb-2">
                        {parcel.vendor.logo?.resized?.sm && (
                          <img
                            src={parcel.vendor.logo.resized.sm}
                            alt={parcel.vendor.title}
                            className="w-6 h-6 rounded"
                          />
                        )}
                        <span className="text-xs font-medium text-ds-700">
                          اسم غرفه: {parcel.vendor.title}
                        </span>
                      </div>

                      {/* Items */}
                      <div className="space-y-2">
                        {parcel.items.map((item) => {
                          const isItemSelected =
                            isSelected && selectedItemIds.includes(item.id);
                          const itemImage =
                            item.product.photos?.[0]?.resized?.sm ||
                            'https://via.placeholder.com/50';

                          return (
                            <button
                              type="button"
                              key={item.id}
                              className={`flex w-full items-center gap-3 rounded-ds-8 border p-2.5 text-right transition-colors ${
                                isItemSelected
                                  ? 'border-ds-primary bg-ds-primary-soft'
                                  : 'border-ds-100 bg-ds-white hover:border-ds-200'
                              }`}
                              onClick={() => handleItemToggle(order.id, item.id)}
                            >
                              {/* Checkbox */}
                              <div
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                                  isItemSelected
                                    ? 'border-ds-primary bg-ds-primary'
                                    : 'border-ds-300'
                                }`}
                              >
                                {isItemSelected && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                              </div>

                              {/* Item Image */}
                              <img
                                src={itemImage}
                                alt={item.title}
                                className="h-12 w-12 shrink-0 rounded-ds-6 object-cover"
                              />

                              {/* Item Info */}
                              <div className="flex-1 min-w-0">
                                <div className="truncate text-xs font-medium text-ds-ink">
                                  {item.title}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] text-ds-500">
                                    {item.quantity} عدد
                                  </span>
                                  <span className="text-[10px] font-semibold text-ds-700">
                                    {formatPrice(item.price)} تومان
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {nextCursor && (
        <div className="flex justify-center pt-4">
          <Button
            variant="text"
            onClick={() => loadOrders(nextCursor)}
            disabled={loading}
          >
            {loading ? 'در حال بارگذاری...' : 'بارگذاری بیشتر'}
          </Button>
        </div>
      )}

      {filteredOrders.length === 0 && (
        <div className="py-8 text-center text-xs text-ds-500">
          سفارشی پیدا نشد
        </div>
      )}
    </div>
  );
};
